import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import SiteFooter from '../components/SiteFooter';
import LogoWithBeta from '../components/LogoWithBeta';
import { getNovaFirstMessage } from '../data';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const JOURNEY_OPTIONS = [
  { emoji: '💜', title: "I'm trying to get pregnant naturally", sub: 'Tracking cycles, understanding ovulation, navigating the two-week wait', value: 'ttc' },
  { emoji: '✨', title: "I'm going through fertility treatment", sub: 'IVF, IUI, egg freezing, donor conception — in the medical system', value: 'fertility' },
  { emoji: '🤰', title: "I'm pregnant", sub: 'Any trimester, any path to get here', value: 'pregnancy' },
  { emoji: '👶', title: "I recently had a baby or I'm raising children", sub: 'Fourth trimester through the teen years', value: 'postpartum' },
  { emoji: '🤍', title: "I've experienced a pregnancy loss", sub: 'Miscarriage, stillbirth, TFMR, or recurrent loss — grief support, community, and resources for finding your way forward at your own pace', value: 'loss' },
  { emoji: '💚', title: "I'm a surrogate or exploring surrogacy", sub: "Whether you're an intended parent or a surrogate — agency guidance, legal resources by state, financial planning, and a community who understands this path", value: 'surrogacy' },
];

const CHAPTER_DEPTH = {
  fertility: {
    headline: 'What does your fertility journey look like right now?',
    options: ['Just starting', 'In an IVF or IUI cycle now', 'Navigating a loss or failed cycle', 'Thinking about egg freezing', 'Exploring surrogacy', 'Still figuring out my options'],
  },
  ttc: {
    headline: 'How long have you been trying?',
    options: ['Just started', '3-6 months', '6-12 months', '12+ months'],
  },
  pregnancy: {
    headline: 'How far along are you?',
    options: ['First trimester', 'Second trimester', 'Third trimester'],
  },
  postpartum: {
    headline: 'How old is your youngest child?',
    options: ['Newborn 0-3 months', 'Baby 3-12 months', 'Toddler 1-3 years', 'Child 4-12 years', 'Teen 13-18 years'],
  },
  loss: {
    headline: 'What kind of support are you looking for?',
    options: ['Grief support and community', 'Understanding what happened', 'Finding my way to try again', 'All of the above'],
  },
  surrogacy: {
    headline: 'What is your role?',
    options: ['I am an intended parent', 'I am a surrogate', 'I am exploring both'],
  },
};

const LOSS_OPTIONS = [
  { label: "Yes — and I'd like Nova to remember", value: 'remember', has_loss: true, show_loss_screen: true },
  { label: "Yes — but I'd prefer not to revisit it", value: 'no_revisit', has_loss: true, show_loss_screen: false },
  { label: 'No', value: 'no', has_loss: false, show_loss_screen: false },
  { label: "I'd rather not say", value: 'prefer_not', has_loss: false, show_loss_screen: false },
];

const CHILDREN_OPTIONS = [
  { label: 'No, not yet', value: 'none' },
  { label: "Yes — and I'd love Nova to track their milestones", value: 'track' },
  { label: "I'm pregnant with my first", value: 'pregnant_first' },
  { label: "Yes — but I'd rather not share details", value: 'no_details' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const compactNav = useMediaQuery('(max-width: 900px)');
  const { registerAccount } = useApp();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    journey_type: null,
    journey_detail: null,
    has_loss: false,
    show_loss_screen: false,
    loss_option: null, // remember | no_revisit | no | prefer_not
    children: [], // [{ name?, birth_year }]
    children_option: null, // none | track | pregnant_first | no_details
    proactive_mode: null, // push | in_app
    first_name: '',
    last_name: '',
    date_of_birth: '',
    zip_code: '',
    email: '',
    password: '',
  });

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const skipLossStep = formData.journey_type === 'loss';
  const totalSteps = 7 - (skipLossStep ? 1 : 0);

  const updateForm = (updates) => setFormData((prev) => ({ ...prev, ...updates }));

  const nextStep = () => {
    if (currentStep === 2 && skipLossStep) {
      setCurrentStep(4);
    } else if (currentStep < 7) {
      setCurrentStep((s) => s + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 4 && skipLossStep) {
      setCurrentStep(2);
    } else if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  };

  const canProceedAccount =
    formData.first_name?.trim().length >= 2 &&
    formData.last_name?.trim().length >= 1 &&
    formData.date_of_birth &&
    formData.zip_code?.trim().length >= 5 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email?.trim()) &&
    formData.password?.length >= 8;

  async function createAccount() {
    setError('');
    setBusy(true);
    const novaMessage = getNovaFirstMessage(formData.first_name, formData.journey_type, formData.journey_detail);
    const res = await registerAccount({
      email: formData.email?.trim(),
      password: formData.password,
      first_name: formData.first_name?.trim(),
      last_name: formData.last_name?.trim(),
      date_of_birth: formData.date_of_birth,
      zip_code: formData.zip_code?.trim(),
      journeyType: formData.journey_type,
      journey_detail: formData.journey_detail,
      has_loss: formData.has_loss,
      show_loss_screen: formData.show_loss_screen,
      children: formData.children_option === 'track' ? formData.children : [],
      proactive_mode: formData.proactive_mode,
      loss_support: formData.journey_type === 'loss',
      surrogacy_path: formData.journey_type === 'surrogacy',
      first_nova_message: novaMessage,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error || 'Could not create account.');
      return;
    }
    setCurrentStep(7);
  }

  function goToDashboard() {
    navigate('/');
  }

  const inputBase = {
    width: '100%',
    fontSize: 16,
    border: '1.5px solid var(--border)',
    borderRadius: 10,
    padding: '13px 16px',
    background: 'var(--white)',
    color: 'var(--text)',
    outline: 'none',
    marginBottom: 4,
    boxSizing: 'border-box',
    fontFamily: FONT_BODY,
  };

  const cardStyle = (selected) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    textAlign: 'left',
    padding: '14px 18px',
    border: `1.5px solid ${selected ? '#D4537E' : 'var(--border)'}`,
    borderRadius: 12,
    cursor: 'pointer',
    background: selected ? '#FBEAF0' : 'var(--card)',
    transition: 'all .15s',
    fontFamily: FONT_BODY,
  });

  const depthConfig = CHAPTER_DEPTH[formData.journey_type];
  const progressSteps = skipLossStep ? [1, 2, 4, 5, 6, 7] : [1, 2, 3, 4, 5, 6, 7];
  const progressIndex = progressSteps.indexOf(currentStep);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--white)', display: 'flex', flexDirection: 'column', fontFamily: FONT_BODY }}>
      <div
        style={{
          padding: compactNav ? '14px 18px' : '18px 40px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <LogoWithBeta compact={compactNav} />
        </Link>
      </div>
      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 6, padding: '24px 40px 0' }}>
        {progressSteps.map((s, i) => (
          <div
            key={s}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              background: i < progressIndex ? 'var(--pink)' : i === progressIndex ? 'var(--pink-m)' : 'var(--border)',
              transition: 'background .3s',
            }}
          />
        ))}
      </div>

      <div style={{ maxWidth: currentStep === 1 ? 720 : 520, margin: '0 auto', padding: '48px 24px', flex: 1, width: '100%', boxSizing: 'border-box' }}>
        {/* SCREEN 1: Journey */}
        {currentStep === 1 && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>
              Where are you in your journey right now?
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>
              There's no right answer — just tell Nova where you are today.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {JOURNEY_OPTIONS.map((opt) => (
                <button key={opt.value} type="button" onClick={() => updateForm({ journey_type: opt.value })} style={cardStyle(formData.journey_type === opt.value)}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>{opt.emoji}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35, marginBottom: 4 }}>{opt.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{opt.sub}</div>
                  </div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn-ghost" onClick={() => navigate('/')}>← Back</button>
              <button className="btn-primary" disabled={!formData.journey_type} style={{ opacity: formData.journey_type ? 1 : 0.45 }} onClick={nextStep}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 2: Chapter depth */}
        {currentStep === 2 && depthConfig && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>{depthConfig.headline}</h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>This helps Nova personalize your experience.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {depthConfig.options.map((opt) => (
                <button key={opt} type="button" onClick={() => updateForm({ journey_detail: opt })} style={cardStyle(formData.journey_detail === opt)}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{opt}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn-ghost" onClick={prevStep}>← Back</button>
              <button className="btn-primary" disabled={!formData.journey_detail} style={{ opacity: formData.journey_detail ? 1 : 0.45 }} onClick={nextStep}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 3: Pregnancy loss (skip if journey_type === loss) */}
        {currentStep === 3 && !skipLossStep && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>
              Have you experienced a pregnancy loss?
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>
              You don't have to share this — but if you have, Nova will hold this with care and remember the important dates for you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {LOSS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateForm({ loss_option: opt.value, has_loss: opt.has_loss, show_loss_screen: opt.show_loss_screen })}
                  style={cardStyle(formData.loss_option === opt.value)}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn-ghost" onClick={prevStep}>← Back</button>
              <button className="btn-primary" onClick={nextStep}>Continue →</button>
            </div>
          </div>
        )}

        {/* SCREEN 4: Children */}
        {currentStep === 4 && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>
              Do you have children already?
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>
              Helps Nova know what support you might need — and who to check in on as they grow.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {CHILDREN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateForm({ children_option: opt.value, children: opt.value === 'track' ? [{ name: '', birth_year: '' }] : [] })}
                  style={cardStyle(formData.children_option === opt.value)}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                </button>
              ))}
            </div>
            {formData.children_option === 'track' && (
              <div style={{ marginBottom: 24, padding: 16, background: 'var(--sand)', borderRadius: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Add your children</div>
                {formData.children?.map((child, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <input style={{ ...inputBase, flex: 1 }} placeholder="First name (optional)" value={child.name || ''} onChange={(e) => {
                      const next = [...(formData.children || [])];
                      next[i] = { ...next[i], name: e.target.value };
                      updateForm({ children: next });
                    }} />
                    <input style={{ ...inputBase, width: 100 }} placeholder="Birth year" value={child.birth_year || ''} onChange={(e) => {
                      const next = [...(formData.children || [])];
                      next[i] = { ...next[i], birth_year: e.target.value };
                      updateForm({ children: next });
                    }} />
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ fontSize: 13 }}
                  onClick={() => updateForm({ children: [...(formData.children || []), { name: '', birth_year: '' }] })}
                >
                  + Add another child
                </button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn-ghost" onClick={prevStep}>← Back</button>
              <button className="btn-primary" disabled={formData.children_option === 'track' && (!formData.children?.length || formData.children.some((c) => !c.birth_year))} style={{ opacity: (formData.children_option === 'track' && (!formData.children?.length || formData.children.some((c) => !c.birth_year))) ? 0.45 : 1 }} onClick={nextStep}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 5: Nova introduction */}
        {currentStep === 5 && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>
              One thing Nova does differently
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>
              She doesn't wait for you to come to her.
            </p>
            <div style={{ borderLeft: '4px solid var(--pink)', padding: '16px 20px', background: 'var(--pink-l)', borderRadius: '0 12px 12px 0', marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pink-d)', marginBottom: 8 }}>Nova</div>
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>
                "Based on what you've shared, I'll check in when it matters — before your retrieval, on hard anniversaries, when your child hits a new stage. You can always tell me to back off. But I'd rather show up and not be needed than disappear when you need me."
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
              <button type="button" onClick={() => updateForm({ proactive_mode: 'push' })} style={cardStyle(formData.proactive_mode === 'push')}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Yes — check in on me proactively</div>
              </button>
              <button type="button" onClick={() => updateForm({ proactive_mode: 'in_app' })} style={cardStyle(formData.proactive_mode === 'in_app')}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>In-app only — I'll come to Nova when I need her</div>
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 24 }}>I can change this anytime in settings</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn-ghost" onClick={prevStep}>← Back</button>
              <button className="btn-primary" disabled={!formData.proactive_mode} style={{ opacity: formData.proactive_mode ? 1 : 0.45 }} onClick={nextStep}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 6: Account creation */}
        {currentStep === 6 && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 8, lineHeight: 1.3 }}>
              Create your account
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 24 }}>
              So Nova can remember everything — and reach you at exactly the right moment.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>First name</label>
            <input style={inputBase} placeholder="What Nova will call you" value={formData.first_name} onChange={(e) => updateForm({ first_name: e.target.value })} />
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 16 }}>Last name</label>
            <input style={inputBase} placeholder="Last name" value={formData.last_name} onChange={(e) => updateForm({ last_name: e.target.value })} />
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 16 }}>Date of birth</label>
            <input style={inputBase} placeholder="MM / DD / YYYY" value={formData.date_of_birth} onChange={(e) => updateForm({ date_of_birth: e.target.value })} />
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Helps Nova understand your stage of life and personalize guidance for your age.</p>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 16 }}>Zip code</label>
            <input style={inputBase} placeholder="ZIP code" value={formData.zip_code} onChange={(e) => updateForm({ zip_code: e.target.value })} />
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Used to find doctors, camps, and events near you. Never shared.</p>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 16 }}>Email address</label>
            <input type="email" autoComplete="email" style={inputBase} placeholder="you@example.com" value={formData.email} onChange={(e) => updateForm({ email: e.target.value })} />
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 16 }}>Password</label>
            <input type="password" autoComplete="new-password" style={inputBase} placeholder="8+ characters" value={formData.password} onChange={(e) => updateForm({ password: e.target.value })} />
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16, marginBottom: 24 }}>Your health data is encrypted and private. We never sell it. We never share it.</p>
            {error && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
            <button className="btn-primary" disabled={!canProceedAccount || busy} style={{ width: '100%', opacity: canProceedAccount && !busy ? 1 : 0.45, marginBottom: 16 }} onClick={createAccount}>
              {busy ? 'Creating…' : 'Create my account →'}
            </button>
            <div style={{ marginTop: 24 }}>
              <button className="btn-ghost" onClick={prevStep}>← Back</button>
            </div>
          </div>
        )}

        {/* SCREEN 7: Nova's first message */}
        {currentStep === 7 && (
          <div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 28, color: 'var(--text)', marginBottom: 24, lineHeight: 1.3 }}>
              Nova is ready for you.
            </h2>
            <div style={{ borderLeft: '4px solid var(--pink)', padding: '20px 24px', background: 'var(--pink-l)', borderRadius: '0 12px 12px 0', marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pink-d)', marginBottom: 10 }}>Nova</div>
              <p style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>{getNovaFirstMessage(formData.first_name, formData.journey_type, formData.journey_detail)}</p>
            </div>
            <button className="btn-primary" style={{ width: '100%' }} onClick={goToDashboard}>
              Go to my dashboard →
            </button>
          </div>
        )}
      </div>
      <SiteFooter linkColor="var(--muted)" />
    </div>
  );
}
